// src/screens/CreateTraining.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { CalendarDays, Users, ClipboardList, Save, Clock, Repeat } from "lucide-react";
import i18n from "../i18n";

type Player = { id: string; firstName: string; lastName: string; number: number; };
type Team = { id: string; team_name: string; players: Player[]; };
type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=Dimanche ... 6=Samedi (API JS)

/* ===== i18n mini-hook (safe) ===== */
function useI18nSafe() {
  const [, setLangTick] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLangTick(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, opts?: Record<string, unknown>): string =>
    opts ? String((i18n.t as any)(key, opts)) : String((i18n.t as any)(key));
  return { t };
}

export const CreateTraining = () => {
  const { t } = useI18nSafe();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(new Set());
  const [date, setDate] = useState("");   // "YYYY-MM-DD"
  const [time, setTime] = useState("");   // "HH:MM"
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // ---- Recurrence state ----
  const [recurEnabled, setRecurEnabled] = useState(false);
  const [intervalWeeks, setIntervalWeeks] = useState(1); // toutes les N semaines
  const [weekdays, setWeekdays] = useState<Set<Weekday>>(new Set<Weekday>()); // ex: {1,3} = Lundi, Mercredi
  const [endMode, setEndMode] = useState<"after" | "until">("after");
  const [count, setCount] = useState(6); // occurrences
  const [until, setUntil] = useState(""); // "YYYY-MM-DD"

  // Charger les équipes
  useEffect(() => {
    const fetchTeams = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("teams")
        .select("id, team_name, players")
        .eq("user_id", user.id)
        .or("archived.is.null,archived.eq.false");

      if (error) {
        console.error("Erreur chargement équipes:", error);
        return;
      }

      const safeTeams: Team[] = (data || []).map((t: any) => ({
        id: t.id,
        team_name: t.team_name,
        players: Array.isArray(t.players) ? t.players : [],
      }));

      setTeams(safeTeams);
      if (safeTeams.length > 0) setSelectedTeamId(safeTeams[0].id);
    };
    fetchTeams();
  }, []);

  // Mettre à jour les joueurs quand l’équipe change
  useEffect(() => {
    const team = teams.find((t) => t.id === selectedTeamId);
    setPlayers(team?.players || []);
    setSelectedPlayers(new Set()); // reset sélection quand on change d’équipe
  }, [selectedTeamId, teams]);

  const togglePlayer = (playerId: string) => {
    setSelectedPlayers((prev) => {
      const copy = new Set(prev);
      copy.has(playerId) ? copy.delete(playerId) : copy.add(playerId);
      return copy;
    });
  };

  // Helpers dates
  const parseLocalDateTime = (d: string, tStr: string) => {
    const [y, m, day] = d.split("-").map(Number);
    const [hh, mm] = tStr.split(":").map(Number);
    return new Date(y, (m - 1), day, hh ?? 0, mm ?? 0, 0, 0);
  };

  // local + offset explicite (ex: 2025-09-01T19:00:00+02:00)
  const fmtLocalWithOffset = (dt: Date) => {
    const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
    const y = dt.getFullYear();
    const m = pad(dt.getMonth() + 1);
    const d = pad(dt.getDate());
    const hh = pad(dt.getHours());
    const mm = pad(dt.getMinutes());
    const offsetMin = -dt.getTimezoneOffset();
    const sign = offsetMin >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMin);
    const oh = pad(Math.floor(abs / 60));
    const om = pad(abs % 60);
    return `${y}-${m}-${d}T${hh}:${mm}:00${sign}${oh}:${om}`;
  };

  const weekLabels = useMemo(
    () => [
      { lab: t("createTraining:weekdays.monShort"), val: 1 as Weekday },
      { lab: t("createTraining:weekdays.tueShort"), val: 2 as Weekday },
      { lab: t("createTraining:weekdays.wedShort"), val: 3 as Weekday },
      { lab: t("createTraining:weekdays.thuShort"), val: 4 as Weekday },
      { lab: t("createTraining:weekdays.friShort"), val: 5 as Weekday },
      { lab: t("createTraining:weekdays.satShort"), val: 6 as Weekday },
      { lab: t("createTraining:weekdays.sunShort"), val: 0 as Weekday },
    ],
    [t]
  );

  const toggleWeekday = (w: Weekday) => {
    setWeekdays((prev) => {
      const next = new Set(prev);
      next.has(w) ? next.delete(w) : next.add(w);
      return next;
    });
  };

  const generateOccurrences = (): Date[] => {
    if (!recurEnabled) {
      if (!date || !time) return [];
      return [parseLocalDateTime(date, time)];
    }
    if (!date || !time || intervalWeeks < 1) return [];
    const start = parseLocalDateTime(date, time);

    const selectedDays = (weekdays.size > 0 ? Array.from(weekdays) : [start.getDay() as Weekday])
      .sort((a, b) => a - b);

    const out: Date[] = [];

    if (endMode === "after") {
      const LIMIT = Math.min(Math.max(count, 1), 200);
      let occurrencesAdded = 0;
      const base = new Date(start);
      base.setHours(0, 0, 0, 0);

      let weekIndex = 0;
      while (occurrencesAdded < LIMIT) {
        const weekStart = new Date(base);
        weekStart.setDate(weekStart.getDate() + weekIndex * 7 * intervalWeeks);

        for (const wd of selectedDays) {
          const d = new Date(weekStart);
          const curWday = d.getDay();
          d.setDate(d.getDate() - curWday + wd);
          d.setHours(start.getHours(), start.getMinutes(), 0, 0);

          if (d < start && weekIndex === 0) continue;

          out.push(new Date(d));
          occurrencesAdded++;
          if (occurrencesAdded >= LIMIT) break;
        }
        weekIndex++;
      }
      out.sort((a, b) => a.getTime() - b.getTime());
      return out;
    } else {
      if (!until) return [start];
      const endDate = parseLocalDateTime(until, time);
      const MAX = 400;

      const outArr: Date[] = [];
      const base = new Date(start);
      base.setHours(0, 0, 0, 0);

      let weekIndex = 0;
      let guard = 0;
      while (guard < MAX) {
        const weekStart = new Date(base);
        weekStart.setDate(weekStart.getDate() + weekIndex * 7 * intervalWeeks);

        for (const wd of selectedDays) {
          const d = new Date(weekStart);
          const curWday = d.getDay();
          d.setDate(d.getDate() - curWday + wd);
          d.setHours(start.getHours(), start.getMinutes(), 0, 0);

          if (d < start && weekIndex === 0) continue;
          if (d > endDate) {
            outArr.sort((a, b) => a.getTime() - b.getTime());
            return outArr;
          }

          outArr.push(new Date(d));
          guard++;
          if (guard >= MAX) break;
        }
        weekIndex++;
      }
      outArr.sort((a, b) => a.getTime() - b.getTime());
      return outArr;
    }
  };

  const handleSubmit = async () => {
    if (!selectedTeamId || !date || !time) return;
    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setSaving(false); return; }

    try {
      const occurrences = generateOccurrences();
      if (occurrences.length === 0) {
        alert(t("createTraining:alerts.noOccurrences"));
        setSaving(false);
        return;
      }

      const rows = occurrences.map((dt) => ({
        user_id: user.id,
        team_id: selectedTeamId,
        date: fmtLocalWithOffset(dt),
        notes,
      }));

      const { data: created, error: trainingsErr } = await supabase
        .from("trainings")
        .insert(rows)
        .select();

      if (trainingsErr || !created || created.length === 0) {
        console.error(trainingsErr);
        alert(t("createTraining:alerts.createError"));
        setSaving(false);
        return;
      }

      const selectedList = Array.from(selectedPlayers);
      if (selectedList.length > 0) {
        const attendanceRows = created.flatMap((tr: any) =>
          selectedList.map((player_id) => ({
            training_id: tr.id,
            player_id,
            was_present: true,
          }))
        );
        const { error: attErr } = await supabase.from("training_attendance").insert(attendanceRows);
        if (attErr) console.error("Erreur enregistrement présences:", attErr);
      }

      navigate("/trainings");
    } finally {
      setSaving(false);
    }
  };

  const canSave = !!selectedTeamId && !!date && !!time && !saving;

  return (
    <section className="min-h-screen w-full p-6 mx-auto bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <h1 className="text-2xl font-bold mb-6 text-center text-orange-600 dark:text-orange-400">
        {t("createTraining:title")}
      </h1>

      <div className="max-w-3xl mx-auto rounded-2xl border bg-white border-gray-200 shadow-sm p-6 dark:bg-gray-900 dark:border-gray-800">
        {/* Équipe */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
            {t("createTraining:team.label")}
          </label>
          <div className="relative">
            <Users className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
            <select
              value={selectedTeamId ?? ""}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="w-full rounded-lg pl-9 pr-3 py-2 border
                         bg-white border-gray-300 text-gray-900
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.team_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
            {t("creatematch:fields.date")}
          </label>
          <div className="relative">
            <CalendarDays className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full appearance-none text-sm rounded-lg pl-9 pr-3 py-2 border
                 bg-white border-gray-300 text-gray-900
                 focus:outline-none focus:ring-2 focus:ring-orange-400/60
                 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
          </div>
        </div>

        {/* Heure */}
        <div>
          <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
            {t("creatematch:fields.time")}
          </label>
          <div className="relative">
            <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              step={60}
              className="w-full appearance-none text-sm rounded-lg pl-9 pr-3 py-2 border
                 bg-white border-gray-300 text-gray-900
                 focus:outline-none focus:ring-2 focus:ring-orange-400/60
                 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
            />
          </div>
        </div>

        {/* Récurrence */}
        <div className="mb-6 rounded-xl border p-4 dark:border-gray-800">
          <label className="flex items-center gap-2 font-semibold mb-3">
            <Repeat className="w-4 h-4" />
            {t("createTraining:recurrence.title")}
          </label>

          <label className="flex items-center gap-2 mb-3">
            <input
              type="checkbox"
              checked={recurEnabled}
              onChange={(e) => setRecurEnabled(e.target.checked)}
              className="h-4 w-4 accent-orange-500"
            />
            <span>{t("createTraining:recurrence.enableLabel")}</span>
          </label>

          {recurEnabled && (
            <>
              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{t("createTraining:recurrence.every_weeks_prefix")}</span>
                  <input
                    type="number"
                    min={1}
                    value={intervalWeeks}
                    onChange={(e) => setIntervalWeeks(Math.max(1, Number(e.target.value || 1)))}
                    className="w-16 rounded border px-2 py-1 dark:bg-gray-800 dark:border-gray-700"
                  />
                  <span className="text-sm">{t("createTraining:recurrence.every_weeks_suffix")}</span>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {weekLabels.map(({ lab, val }) => {
                    const checked = weekdays.has(val);
                    return (
                      <button
                        type="button"
                        key={val}
                        onClick={() => toggleWeekday(val)}
                        className={`px-2 py-1 rounded border text-sm ${checked
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
                          }`}
                      >
                        {lab}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="endmode"
                      value="after"
                      checked={endMode === "after"}
                      onChange={() => setEndMode("after")}
                    />
                    <span className="text-sm">{t("createTraining:recurrence.end.after.label")}</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={count}
                    onChange={(e) => setCount(Math.max(1, Number(e.target.value || 1)))}
                    disabled={endMode !== "after"}
                    className="w-20 rounded border px-2 py-1 disabled:opacity-60 dark:bg-gray-800 dark:border-gray-700"
                  />
                  <span className="text-sm">{t("createTraining:recurrence.end.after.occurrencesSuffix")}</span>
                </div>

                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="endmode"
                      value="until"
                      checked={endMode === "until"}
                      onChange={() => setEndMode("until")}
                    />
                    <span className="text-sm">{t("createTraining:recurrence.end.until.label")}</span>
                  </label>
                  <input
                    type="date"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                    disabled={endMode !== "until"}
                    className="rounded border px-2 py-1 disabled:opacity-60 dark:bg-gray-800 dark:border-gray-700"
                  />
                </div>
              </div>

              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {t("createTraining:recurrence.hint_no_day")}
              </p>
            </>
          )}
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-1 text-gray-800 dark:text-gray-200">
            {t("createTraining:notes.label")}
          </label>
          <div className="relative">
            <ClipboardList className="w-4 h-4 absolute left-3 top-3 text-gray-500 dark:text-gray-400" />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-lg pl-9 pr-3 py-2 border
                         bg-white border-gray-300 text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              placeholder={t("createTraining:notes.placeholder")}
            />
          </div>
        </div>

        {/* Présents */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2 text-gray-800 dark:text-gray-200">
            {t("createTraining:presents.label")}
          </label>
          {players.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("createTraining:presents.none")}
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {players.map((player) => {
                const checked = selectedPlayers.has(player.id);
                return (
                  <label
                    key={player.id}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition ${checked
                      ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-900/30 dark:text-green-300"
                      : "bg-white border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePlayer(player.id)}
                      className="h-4 w-4 accent-orange-500"
                    />
                    <span className="text-sm">
                      #{player.number} — {player.firstName} {player.lastName}
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Enregistrer */}
        <button
          onClick={handleSubmit}
          disabled={!canSave}
          className={`mt-4 inline-flex items-center gap-2 font-semibold py-3 px-6 rounded-lg transition
                      focus:outline-none focus:ring-2 focus:ring-orange-400/60 ${canSave
              ? "bg-[#fc7825] hover:bg-orange-600 text-white"
              : "bg-orange-300 text-white cursor-not-allowed"
            }`}
        >
          <Save className="w-4 h-4" />
          {recurEnabled ? t("createTraining:save.series") : t("createTraining:save.single")}
        </button>
      </div>
    </section>
  );
};
